import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '..';
import { ref, onValue, goOnline } from 'firebase/database';
import { database } from '../../services/firebase/core';
import logger from '../../utils/logger';

// Type definitions matching original Marune structure
export interface TechniqueData {
  name: string;
  sortname: string;
  type: 'move' | 'position';
  isTrainingWikiTech: boolean;
}

export interface PersonalWikiData {
  dateAdded: number;
  lastTrainedOn?: number;
  lastUsed?: number;
  timesTrained?: number;
  timesUsed?: number;
  isPublic?: boolean;
  isVisible?: boolean;
  isTrainingWikiTech?: boolean;
  notes?: Record<string, {
    dateCreated: number;
    dateUpdated: number;
    text: string;
  }>;
  photos?: Record<string, {
    dateAdded: number;
    url: string;
    thumbURL?: string;
    aspectRatio?: number;
    caption?: string;
  }>;
}

// State structure matching original app
interface TrainingWikiState {
  // Technique keys sorted by lastTrainedOn (newest first)
  sortedTechKeys: string[];
  
  // Technique keys sorted alphabetically by sortname
  alphaSortedTechKeys: string[];
  
  // All technique data (Core + Community)
  techniques: Record<string, TechniqueData>;
  
  // User's personal wiki data
  personalWiki: Record<string, PersonalWikiData>;
  
  // Loading state
  isLoading: boolean;
  
  // Error state
  error: string | null;
  
  // Track active listeners for cleanup
  activeListeners: Array<() => void>;
}

// Keep track of listeners outside Redux for cleanup
const listeners: {
  personalWiki: (() => void) | null;
} = {
  personalWiki: null
};

const initialState: TrainingWikiState = {
  sortedTechKeys: [],
  alphaSortedTechKeys: [],
  techniques: {},
  personalWiki: {},
  isLoading: false,
  error: null,
  activeListeners: []
};

// Thunk to load all training wiki data (Core + Community + Personal)
export const loadPersonalWiki = createAsyncThunk(
  'trainingWiki/loadPersonalWiki',
  async (userId: string, { dispatch, getState }) => {
    try {
      logger.info('Loading personal wiki data for user:', userId);
      dispatch(trainingWikiSlice.actions.setLoading(true));
      
      // Ensure database is online
      goOnline(database);
      
      // First, set up listener for personal wiki to get user's technique list
      const personalWikiRef = ref(database, `personalWiki/${userId}`);
      
      // Clear any existing listener
      if (listeners.personalWiki) {
        listeners.personalWiki();
        listeners.personalWiki = null;
      }
      
      const listener = onValue(personalWikiRef, async (snapshot) => {
        const personalWikiData = snapshot.val() || {};
        const personalWikiKeys = Object.keys(personalWikiData);
        logger.info('Personal wiki data received:', personalWikiKeys.length, 'techniques');
        
        // Dispatch action to store personal wiki data
        dispatch(trainingWikiSlice.actions.setPersonalWiki(personalWikiData));
        
        if (personalWikiKeys.length === 0) {
          // User has no techniques in their wiki
          dispatch(trainingWikiSlice.actions.setSortedKeys([]));
          dispatch(trainingWikiSlice.actions.setAlphaSortedKeys([]));
          dispatch(trainingWikiSlice.actions.setLoading(false));
          return;
        }
        
        // Get current techniques from state
        const state = getState() as RootState;
        const currentTechniques = state.trainingWiki.techniques;
        
        // Find which techniques we need to fetch
        const unfetchedTechniques = personalWikiKeys.filter(key => !currentTechniques[key]);
        
        if (unfetchedTechniques.length === 0) {
          // All techniques already loaded
          updateSortedKeys();
          return;
        }
        
        logger.info('Need to fetch', unfetchedTechniques.length, 'techniques');
        
        // Fetch technique data for unfetched techniques only
        const techniquePromises = unfetchedTechniques.map(async (techKey) => {
          const isTrainingWikiTech = personalWikiData[techKey]?.isTrainingWikiTech;
          
          if (isTrainingWikiTech) {
            // Fetch from trainingWikiTechniques
            const techRef = ref(database, `trainingWikiTechniques/${techKey}/techniqueData`);
            try {
              const techSnap = await new Promise<any>((resolve) => {
                onValue(techRef, (snap) => resolve(snap), { onlyOnce: true });
              });
              
              if (techSnap.exists()) {
                const techData = techSnap.val();
                return {
                  key: techKey,
                  data: {
                    name: techData.name || '',
                    sortname: techData.sortname || techData.name?.toLowerCase() || '',
                    type: techData.type || 'move',
                    isTrainingWikiTech: true
                  }
                };
              }
            } catch (error) {
              logger.error('Error fetching TWT technique', techKey, error);
            }
          } else {
            // Fetch from community techniques
            const techRef = ref(database, `techniques/${techKey}`);
            try {
              const techSnap = await new Promise<any>((resolve) => {
                onValue(techRef, (snap) => resolve(snap), { onlyOnce: true });
              });
              
              if (techSnap.exists()) {
                const techData = techSnap.val();
                return {
                  key: techKey,
                  data: {
                    name: techData.name || '',
                    sortname: techData.sortname || techData.name?.toLowerCase() || '',
                    type: techData.type || 'move',
                    isTrainingWikiTech: false
                  }
                };
              }
            } catch (error) {
              logger.error('Error fetching community technique', techKey, error);
            }
          }
          
          return null;
        });
        
        // Wait for all technique fetches to complete
        const fetchedTechniques = await Promise.all(techniquePromises);
        
        // Build new techniques object
        const newTechniques: Record<string, TechniqueData> = {};
        fetchedTechniques.forEach((result) => {
          if (result) {
            newTechniques[result.key] = result.data;
          }
        });
        
        // Merge with existing techniques
        const allTechniques = { ...currentTechniques, ...newTechniques };
        dispatch(trainingWikiSlice.actions.setTechniques(allTechniques));
        
        // Update sorted keys
        updateSortedKeys();
        
        function updateSortedKeys() {
          // Generate sorted keys for display
          // Personal wiki techniques sorted by MRU (Most Recently Used)
          const sortedPersonalWikiKeys = personalWikiKeys
            .filter(key => allTechniques[key]) // Only include techniques that exist
            .sort((a, b) => {
              const aLastTrained = personalWikiData[a].lastTrainedOn || personalWikiData[a].dateAdded || 0;
              const bLastTrained = personalWikiData[b].lastTrainedOn || personalWikiData[b].dateAdded || 0;
              return bLastTrained - aLastTrained;
            });
          
          // Also generate alphabetically sorted keys
          const alphaSortedKeys = personalWikiKeys
            .filter(key => allTechniques[key])
            .sort((a, b) => {
              const aName = allTechniques[a]?.sortname || '';
              const bName = allTechniques[b]?.sortname || '';
              return aName.localeCompare(bName);
            });
          
          dispatch(trainingWikiSlice.actions.setSortedKeys(sortedPersonalWikiKeys));
          dispatch(trainingWikiSlice.actions.setAlphaSortedKeys(alphaSortedKeys));
          dispatch(trainingWikiSlice.actions.setLoading(false));
        }
      }, (error) => {
        logger.error('Error listening to personal wiki:', error);
        dispatch(trainingWikiSlice.actions.setError('Failed to load personal wiki data'));
        dispatch(trainingWikiSlice.actions.setLoading(false));
      });
      
      // Store listener reference
      listeners.personalWiki = listener;
      
      logger.info('Personal wiki listener set up');
      
    } catch (error) {
      logger.error('Error loading training wiki:', error);
      dispatch(trainingWikiSlice.actions.setError(error instanceof Error ? error.message : 'Failed to load training wiki'));
      dispatch(trainingWikiSlice.actions.setLoading(false));
      throw error;
    }
  }
);

const trainingWikiSlice = createSlice({
  name: 'trainingWiki',
  initialState,
  reducers: {
    setPersonalWiki: (state, action: PayloadAction<Record<string, PersonalWikiData>>) => {
      state.personalWiki = action.payload;
    },
    
    setTechniques: (state, action: PayloadAction<Record<string, TechniqueData>>) => {
      state.techniques = action.payload;
    },
    
    setSortedKeys: (state, action: PayloadAction<string[]>) => {
      state.sortedTechKeys = action.payload;
    },
    
    setAlphaSortedKeys: (state, action: PayloadAction<string[]>) => {
      state.alphaSortedTechKeys = action.payload;
    },
    
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    
    resetTrainingWiki: () => initialState,
    
    clearAllListeners: (state) => {
      state.activeListeners.forEach(unsubscribe => unsubscribe());
      state.activeListeners = [];
    }
  }
});

// Selectors
export const selectSortedTechKeys = (state: RootState) => state.trainingWiki.sortedTechKeys;
export const selectAlphaSortedTechKeys = (state: RootState) => state.trainingWiki.alphaSortedTechKeys;
export const selectTechniques = (state: RootState) => state.trainingWiki.techniques;
export const selectPersonalWiki = (state: RootState) => state.trainingWiki.personalWiki;
export const selectIsLoading = (state: RootState) => state.trainingWiki.isLoading;
export const selectError = (state: RootState) => state.trainingWiki.error;

export const selectTechniqueWithStats = (techniqueKey: string) => (state: RootState) => {
  const technique = state.trainingWiki.techniques[techniqueKey];
  const personalStats = state.trainingWiki.personalWiki[techniqueKey];
  
  return technique ? {
    ...technique,
    personalStats
  } : null;
};

export const { resetTrainingWiki, clearAllListeners } = trainingWikiSlice.actions;
export default trainingWikiSlice.reducer;
